/**
 * Precheckout Storage Manager
 * Maneja la persistencia de datos del formulario usando localStorage + cart.attributes
 */

class PrecheckoutStorage {
  constructor() {
    this.storageKey = 'tmx_precheckout_data';
    this.debounceTimer = null;
    this.debounceDelay = 1000; // 1 segundo de delay para evitar requests excesivos
  }

  /**
   * Obtener todos los datos del formulario actual
   */
  getFormData(formId) {
    const form = document.getElementById(formId);
    if (!form) return null;

    const data = {
      comprobante: document.querySelector(`[name="attributes[Comprobante de pago]"]:checked`)?.value || '',
      tipoDocumento: form.querySelector('[name="attributes[Tipo de documento]"]')?.value || '',
      numeroDocumento: form.querySelector('[name="attributes[Número de documento]"]')?.value || '',
      razonSocial: form.querySelector('[name="attributes[Razón Social]"]')?.value || '',
      ruc: form.querySelector('[name="attributes[RUC]"]')?.value || '',
      departamento: form.querySelector('[name="attributes[Departamento]"]')?.value || '',
      provincia: form.querySelector('[name="attributes[Provincia]"]')?.value || '',
      distrito: form.querySelector('[name="attributes[Distrito]"]')?.value || '',
      ubigeo: form.querySelector('[name="attributes[Ubigeo]"]')?.value || '',
      note: form.querySelector('[name="note"]')?.value || ''
    };

    return data;
  }

  /**
   * Guardar datos en localStorage
   */
  saveToLocalStorage(data) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
      console.log('[PrecheckoutStorage] Datos guardados en localStorage');
    } catch (error) {
      console.error('[PrecheckoutStorage] Error guardando en localStorage:', error);
    }
  }

  /**
   * Obtener datos desde localStorage
   */
  getFromLocalStorage() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('[PrecheckoutStorage] Error leyendo localStorage:', error);
      return null;
    }
  }

  /**
   * Actualizar cart.attributes en el servidor
   */
  async updateCartAttributes(data) {
    try {
      const attributes = {
        // Atributos nuevos (en español)
        'Comprobante de pago': data.comprobante,
        'Tipo de documento': data.tipoDocumento,
        'Número de documento': data.numeroDocumento,
        'Razón Social': data.razonSocial,
        'RUC': data.ruc,
        'Departamento': data.departamento,
        'Provincia': data.provincia,
        'Distrito': data.distrito,
        'Ubigeo': data.ubigeo,
        
        // Limpiar atributos antiguos (en inglés) - establecerlos como vacíos
        'document_type': '',
        'payment_receipt': '',
        'document_number': '',
        'department': '',
        'province': '',
        'district': '',
        'ubigeo': '' // Este puede estar duplicado
      };

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes })
      });

      if (response.ok) {
        console.log('[PrecheckoutStorage] Cart attributes actualizados y limpiados');
      }
    } catch (error) {
      console.error('[PrecheckoutStorage] Error actualizando cart:', error);
    }
  }

  /**
   * Guardar datos (localStorage + cart.attributes con debounce)
   */
  save(formId) {
    const data = this.getFormData(formId);
    if (!data) return;

    // 1. Guardar inmediatamente en localStorage
    this.saveToLocalStorage(data);

    // 2. Actualizar cart.attributes con debounce (evitar requests excesivos)
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.updateCartAttributes(data);
    }, this.debounceDelay);
  }

  /**
   * Restaurar datos en el formulario
   */
  restore(formId, cartAttributes) {
    let data = null;

    // Prioridad 1: cart.attributes (si existe y tiene datos)
    if (cartAttributes && cartAttributes['Comprobante de pago']) {
      data = {
        comprobante: cartAttributes['Comprobante de pago'] || '',
        tipoDocumento: cartAttributes['Tipo de documento'] || '',
        numeroDocumento: cartAttributes['Número de documento'] || '',
        razonSocial: cartAttributes['Razón Social'] || '',
        ruc: cartAttributes['RUC'] || '',
        departamento: cartAttributes['Departamento'] || '',
        provincia: cartAttributes['Provincia'] || '',
        distrito: cartAttributes['Distrito'] || '',
        ubigeo: cartAttributes['Ubigeo'] || ''
      };
      console.log('[PrecheckoutStorage] Restaurando desde cart.attributes');
    } 
    // Prioridad 2: localStorage (fallback)
    else {
      data = this.getFromLocalStorage();
      if (data) {
        console.log('[PrecheckoutStorage] Restaurando desde localStorage');
      }
    }

    if (!data) {
      console.log('[PrecheckoutStorage] No hay datos guardados');
      return;
    }

    // Restaurar valores en el formulario
    this.fillForm(formId, data);
  }

  /**
   * Llenar formulario con datos guardados
   */
  fillForm(formId, data) {
    const form = document.getElementById(formId);
    if (!form) return;

    // Comprobante de pago (radio buttons)
    if (data.comprobante) {
      const comprobanteRadio = form.querySelector(`[name="attributes[Comprobante de pago]"][value="${data.comprobante}"]`);
      if (comprobanteRadio && !comprobanteRadio.checked) {
        comprobanteRadio.checked = true;
        // Disparar evento change para actualizar campos dependientes
        comprobanteRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Campos de texto y selects (solo si están vacíos)
    const fields = [
      { name: 'attributes[Tipo de documento]', value: data.tipoDocumento },
      { name: 'attributes[Número de documento]', value: data.numeroDocumento },
      { name: 'attributes[Razón Social]', value: data.razonSocial },
      { name: 'attributes[RUC]', value: data.ruc },
      { name: 'attributes[Departamento]', value: data.departamento },
      { name: 'attributes[Provincia]', value: data.provincia },
      { name: 'attributes[Distrito]', value: data.distrito },
      { name: 'attributes[Ubigeo]', value: data.ubigeo },
      { name: 'note', value: data.note }
    ];

    fields.forEach(field => {
      if (!field.value) return;
      
      const input = form.querySelector(`[name="${field.name}"]`);
      if (input && !input.value) {
        input.value = field.value;
        
        // Disparar evento change para campos con lógica dependiente
        if (field.name.includes('Departamento') || field.name.includes('Provincia')) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    console.log('[PrecheckoutStorage] Formulario restaurado');
  }

  /**
   * Inicializar listeners para auto-guardado
   */
  initAutoSave(formId) {
    const form = document.getElementById(formId);
    if (!form) return;

    // Escuchar cambios en todos los campos
    const fields = form.querySelectorAll('input, select, textarea');
    
    fields.forEach(field => {
      // Usar 'input' para cambios inmediatos en texto
      // Usar 'change' para radios, checkboxes y selects
      const eventType = field.type === 'text' || field.type === 'textarea' ? 'input' : 'change';
      
      field.addEventListener(eventType, () => {
        this.save(formId);
      });
    });

    // Cancelar debounce pendiente cuando se hace submit
    // Evita que se actualicen los atributos después del submit
    form.addEventListener('submit', () => {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        console.log('[PrecheckoutStorage] Debounce cancelado antes del submit');
      }
    });

    console.log(`[PrecheckoutStorage] Auto-guardado activado para ${formId}`);
  }

  /**
   * Limpiar datos guardados
   */
  clear() {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('[PrecheckoutStorage] Datos limpiados');
    } catch (error) {
      console.error('[PrecheckoutStorage] Error limpiando datos:', error);
    }
  }

  /**
   * Limpiar atributos antiguos del carrito (en inglés)
   * Llamar una sola vez al cargar la página
   */
  async cleanOldAttributes() {
    try {
      const attributes = {
        'document_type': '',
        'payment_receipt': '',
        'document_number': '',
        'department': '',
        'province': '',
        'district': ''
      };

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes })
      });

      console.log('[PrecheckoutStorage] Atributos antiguos limpiados');
    } catch (error) {
      console.error('[PrecheckoutStorage] Error limpiando atributos antiguos:', error);
    }
  }
}

// Crear instancia global
window.PrecheckoutStorage = new PrecheckoutStorage();

// Limpiar atributos antiguos una sola vez al cargar
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    window.PrecheckoutStorage.cleanOldAttributes();
  });
} else {
  window.PrecheckoutStorage.cleanOldAttributes();
}
